/**
 * Native bridge — Capacitor feature detection + web fallbacks.
 * Import from here instead of @capacitor/* directly so every component
 * works on both web and native without conditional import gymnastics.
 */

import { Capacitor } from '@capacitor/core';

/** True when running inside the native iOS/Android shell */
export const isNative = Capacitor.isNativePlatform();

/** Platform: 'ios' | 'android' | 'web' */
export const platform = Capacitor.getPlatform();

// --------------- Camera ---------------

/**
 * Pick a photo from the camera or gallery.
 * On native: uses Capacitor Camera (native picker, better UX).
 * On web: returns null — caller should fall back to <input type="file">.
 */
export async function pickPhoto(source = 'gallery') {
  if (!isNative) return null;

  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  const result = await Camera.getPhoto({
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
    width: 1200,
  });

  // Convert the native URI to a File object so the rest of the upload
  // pipeline (EXIF extraction, Cloudinary upload) works unchanged.
  const response = await fetch(result.webPath);
  const blob = await response.blob();
  const filename = `photo_${Date.now()}.${result.format || 'jpeg'}`;
  return new File([blob], filename, { type: `image/${result.format || 'jpeg'}` });
}

/**
 * Pick multiple photos from the gallery (native only).
 * Returns an array of File objects, or null on web.
 */
export async function pickMultiplePhotos() {
  if (!isNative) return null;

  const { Camera } = await import('@capacitor/camera');
  const result = await Camera.pickImages({
    quality: 90,
    width: 1200,
  });

  const files = await Promise.all(
    result.photos.map(async (photo, i) => {
      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const filename = `photo_${Date.now()}_${i}.${photo.format || 'jpeg'}`;
      return new File([blob], filename, { type: `image/${photo.format || 'jpeg'}` });
    })
  );

  return files;
}

// --------------- Haptics ---------------

/** Trigger a light haptic tap (native only, no-op on web) */
export async function hapticTap() {
  if (!isNative) return;
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
  await Haptics.impact({ style: ImpactStyle.Light });
}

/** Trigger a success haptic (native only) */
export async function hapticSuccess() {
  if (!isNative) return;
  const { Haptics, NotificationType } = await import('@capacitor/haptics');
  await Haptics.notification({ type: NotificationType.Success });
}

// --------------- Push Notifications ---------------

/**
 * Register for push notifications and return the device token.
 * Returns null on web or if the user denies permission.
 */
export async function registerPush() {
  if (!isNative) return null;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return null;

  return new Promise((resolve) => {
    PushNotifications.addListener('registration', (token) => {
      resolve(token.value);
    });

    PushNotifications.addListener('registrationError', () => {
      resolve(null);
    });

    PushNotifications.register();
  });
}

/**
 * Listen for incoming push notifications (native only).
 * @param {Function} onNotification - called with notification data
 */
export async function onPushReceived(callback) {
  if (!isNative) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('pushNotificationReceived', callback);
}

/**
 * Listen for push notification taps (native only).
 * @param {Function} onTap - called with notification action data
 */
export async function onPushTapped(callback) {
  if (!isNative) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('pushNotificationActionPerformed', callback);
}

// --------------- Status Bar ---------------

/** Configure the status bar for the app theme */
export async function configureStatusBar() {
  if (!isNative) return;
  const { StatusBar, Style } = await import('@capacitor/status-bar');
  await StatusBar.setStyle({ style: Style.Dark });
  if (platform === 'android') {
    await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
  }
}

// --------------- App (deep links, back button) ---------------

/**
 * Listen for deep link / universal link opens.
 * @param {Function} callback - called with { url: string }
 */
export async function onAppUrl(callback) {
  if (!isNative) return;
  const { App } = await import('@capacitor/app');
  App.addListener('appUrlOpen', callback);
}

/**
 * Listen for Android hardware back button.
 * @param {Function} callback - called on back press
 */
export async function onBackButton(callback) {
  if (!isNative) return;
  const { App } = await import('@capacitor/app');
  App.addListener('backButton', callback);
}
